import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, inject, OnInit, viewChild, ViewChild } from '@angular/core';
import { FormsModule, NgForm, NgModel } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from '../../../../core/services/auth.service';
import { User } from '../../../../core/models/user.interface';
import { SALARY_RANGES, SUBSCRIPTION_TYPES } from '../../../../core/shared/constants';
import { UsersService } from '../../../../core/services/users.service';
import { environment } from '../../../../../environments/environment';
import { LoaderService } from '../../../../core/shared/services/loader.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { BaseComponent } from '../../../../core/shared/base.component';

@Component({
  selector: 'app-edit-profile',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, FormsModule],
  templateUrl: './edit-profile.component.html',
  styleUrl: './edit-profile.component.scss',
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({ height: 0, padding: 0, opacity: 0, margin: 0, overflow: 'hidden' })),
      state('expanded', style({ height: '*', opacity: 1 })),
      transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')),
    ])
  ]
})
export class EditProfileComponent extends BaseComponent implements OnInit, AfterViewInit {
  private authService: AuthService = inject(AuthService);
  private userService: UsersService = inject(UsersService);
  user: User | any = { personalSection: {}, professionalSection:{professionalData:{}} };
  initialUserData: User | any = {}; // Para comparar cambios
  currentUserId: string = '';
  loader = inject(LoaderService);

  // Variables para manejo de foto de perfil
  profilePhotoUrl: string | null = null;
  selectedPhotoFile: File | null = null;
  photoChanged: boolean = false;

  //Responsive
  sectionsState = ['expanded', 'expanded', 'expanded'];
  isResponsive: boolean = false;
  debounceResize: any;

  isSelectedLaburo: boolean = false;
  salaryOptions: string[] = ['0 - 10 000', '10 001 - 20 000', 'Más de 20 001'];
  selectedSalaryOption: number | null = null;
  isEditName: boolean = false;
  // isEditPassword: boolean = false;
  invalidFields: string[] = [];
  showGlobalError: boolean = false;
  globalErrorMessage: string = '';
  
  // Nuevas propiedades para manejo de modos de edición
  isEditMode: boolean = false;
  isPersonalEditMode: boolean = false;
  isProfessionalEditMode: boolean = false;
  @ViewChild('lastName', { static: false }) lastNameInput!: NgModel;
  @ViewChild('phone', { static: false }) phoneInput!: NgModel;
  @ViewChild('hobbys', { static: false }) hobbysInput!: NgModel;
  @ViewChild('colors', { static: false }) colorsInput!: NgModel;
  @ViewChild('travelDestination', { static: false }) travelDestinationInput!: NgModel;
  @ViewChild('newActivities', { static: false }) newActivitiesInput!: NgModel;
  @ViewChild('skills', { static: false }) skillsInput!: NgModel;
  @ViewChild('futureProjects', { static: false }) futureProjectsInput!: NgModel;
  @ViewChild('photoFileInput', { static: false }) photoFileInput!: ElementRef;
  
  // ViewChild para campos de la sección profesional
  @ViewChild('situacionLaboral', { static: false }) situacionLaboralInput!: NgModel;
  @ViewChild('salaryRange', { static: false }) salaryRangeInput!: NgModel;
  @ViewChild('company', { static: false }) companyInput!: NgModel;
  @ViewChild('role', { static: false }) roleInput!: NgModel;
  
  // ElementRef para hacer focus
  @ViewChild('situacionLaboral', { static: false, read: ElementRef }) situacionLaboralElement!: ElementRef;
  @ViewChild('salaryRange', { static: false, read: ElementRef }) salaryRangeElement!: ElementRef;
  @ViewChild('company', { static: false, read: ElementRef }) companyElement!: ElementRef;
  @ViewChild('role', { static: false, read: ElementRef }) roleElement!: ElementRef;

  // Arrays con opciones para los dropdowns de información profesional
  situacionLaboralOptions: string[] = ['Dependiente', 'Independiente', 'Retirado'];
  salaryRangeOptions: string[] = environment.salaryRanges;
  industryOptions: string[] = [
    'Agroindustria / Agricultura / Pesca',
    'Alimentos y Bebidas',
    'Automotriz / Transporte',
    'Banca / Servicios Financieros / Seguros',
    'Comercio / Consumo Masivo / Retail',
    'Construcción / Inmobiliaria',
    'Consultoría / Servicios Profesionales',
    'Educación / Formación / Capacitación',
    'Energía / Minería / Petróleo / Gas',
    'Gobierno / Sector Público / ONG',
    'Hospitalidad / Turismo / Hotelería',
    'Logística / Cadena de Suministro',
    'Manufactura / Producción Industrial',
    'Marketing / Publicidad / Medios',
    'Salud / Bienestar / Ciencias de la Vida',
    'Tecnología / Software / Telecomunicaciones',
    'Textil / Moda / Diseño',
    'Otro sector'
  ];
  roleOptions: string[] = [
    'Alta Dirección / C-Level',
    'Gerencia General / Dirección de Área',
    'Jefatura / Coordinación',
    'Profesional Especializado',
    'Analista / Consultor',
    'Emprendedor(a) / Empresario(a)',
    'Docente / Investigador',
    'Independiente / Freelance',
    'En transición laboral',
    'Otro rol'
  ];
  skillsOptions: string[] = [
    'Liderazgo y toma de decisiones',
    'Comunicación efectiva',
    'Resolución de problemas',
    'Gestión de equipos',
    'Pensamiento estratégico',
    'Habilidades digitales / tecnología',
    'Creatividad e innovación',
    'Inteligencia emocional',
    'Negociación e influencia',
    'Organización y gestión del tiempo',
    'Otro'
  ];

  // Arrays con opciones para los dropdowns de información personal
  laboralOptions: string[] = ['Sí', 'No', 'Parcialmente'];
  communityOptions: string[] = ['Sí', 'No'];
  communityTypeOptions: string[] = [
    'Comunidad profesional',
    'Comunidad de bienestar',
    'Comunidad educativa',
    'Comunidad social',
    'Comunidad de voluntariado'
  ];
  interactionOptions: string[] = [
    'Presenciales',
    'Virtuales',
    'Hibridas',
    'Uno a uno',
    'Grupales'
  ];

  // Variables para límites de caracteres
  maxPhoenixExpectationsLength: number = 150;

  // Variable para controlar el modal de perfil completado
  showProfileCompleteModal: boolean = false;

  // Variables para manejar selecciones múltiples y lógica condicional
  selectedSkillsNow: string[] = [];
  selectedSkillsFuture: string[] = [];
  otherSkillNow: string = '';
  otherSkillFuture: string = '';
  showOtherSkillNow: boolean = false;
  showOtherSkillFuture: boolean = false;
  showSkillsNowDropdown: boolean = false;
  showSkillsFutureDropdown: boolean = false;

  // Variables para campos "Otro" personalizados
  showOtherIndustry: boolean = false;
  otherIndustry: string = '';
  showOtherCommunityType: boolean = false;
  otherCommunityType: string = '';
  showOtherInteractionType: boolean = false;
  otherInteractionType: string = '';
  
  // Variables para controlar dropdowns de selects simples
  showIndustryDropdown: boolean = false;
  showCommunityTypeDropdown: boolean = false;
  showInteractionTypeDropdown: boolean = false;

  // Método para verificar si todos los formularios están completos
  isProfileComplete(): boolean {
    const requiredFields = [
      this.user.firstName,
      this.user.phone,
      this.user.personalSection?.hobbys,
      this.user.personalSection?.colors,
      this.user.personalSection?.travelDestination,
      this.user.professionalSection?.situacionLaboral
    ];

    // Campos específicos según la situación laboral
    if (this.shouldShowWorkerFields()) {
      requiredFields.push(
        this.user.professionalSection?.professionalData?.salaryRange,
        this.user.professionalSection?.professionalData?.company,
        this.user.professionalSection?.professionalData?.job,
        this.user.professionalSection?.skills,
        this.user.professionalSection?.skillsNow,
        this.user.professionalSection?.skillsFuture
      );
    } else if (this.shouldShowRetiredFields()) {
      requiredFields.push(
        this.user.professionalSection?.ultimoCargo,
        this.user.professionalSection?.ultimaEmpresa
      );
    }

    // Si newActivities está presente, también es obligatorio
    if (this.newActivitiesInput) {
      requiredFields.push(this.user.personalSection?.newActivities);
    }

    // Verificar que todos los campos obligatorios estén llenos
    //return requiredFields.every(field => field && field.trim() !== '');
    console.log('isCompleteInfo', localStorage.getItem('isCompleteInfo'));
    return localStorage.getItem('isCompleteInfo') === 'true';
  }

  // Método para obtener caracteres restantes
  getRemainingChars(): number {
    const currentLength = this.user.personalSection?.phoenixExpectations?.length || 0;
    return this.maxPhoenixExpectationsLength - currentLength;
  }

  // Método para calcular el porcentaje de completitud del perfil
  calculateProfileProgress(): number {
    let completedFields = 0;
    let totalFields = 0;
    const missingFields: string[] = [];

    console.log('=== CALCULANDO PORCENTAJE DE PERFIL ===');

    // Sección 1: Mi cuenta (4 campos)
    totalFields += 4;
    console.log('--- Sección 1: Mi cuenta ---');
    if (this.user.firstName) {
      completedFields++;
      console.log('✓ firstName:', this.user.firstName);
    } else {
      missingFields.push('Nombres y Apellidos');
      console.log('✗ firstName: FALTANTE');
    }
    
    if (this.user.email) {
      completedFields++;
      console.log('✓ email:', this.user.email);
    } else {
      missingFields.push('Correo electrónico');
      console.log('✗ email: FALTANTE');
    }
    
    if (this.user.phone) {
      completedFields++;
      console.log('✓ phone:', this.user.phone);
    } else {
      missingFields.push('Número de teléfono');
      console.log('✗ phone: FALTANTE');
    }
    
    if (this.user.profile?.LinkedInURL) {
      completedFields++;
      console.log('✓ LinkedInURL:', this.user.profile.LinkedInURL);
    } else {
      missingFields.push('URL de LinkedIn');
      console.log('✗ LinkedInURL: FALTANTE');
    }

    // Sección 2: Información profesional
    console.log('--- Sección 2: Información profesional ---');
    // Campos base (2 campos)
    totalFields += 2;
    const situacionLaboral = this.getCurrentSituacionLaboral();
    if (situacionLaboral) {
      completedFields++;
      console.log('✓ situacionLaboral:', situacionLaboral);
    } else {
      missingFields.push('Situación laboral');
      console.log('✗ situacionLaboral: FALTANTE');
    }
    
    const salaryRange = this.getSalaryRangeText();
    if (salaryRange) {
      completedFields++;
      console.log('✓ salaryRange:', salaryRange);
    } else {
      missingFields.push('Rango salarial');
      console.log('✗ salaryRange: FALTANTE');
    }

    // Campos condicionales según situación laboral
    if (this.shouldShowWorkerFields()) {
      console.log('Usuario es trabajador activo/independiente');
      // Para trabajadores activos: company, job, skills, skillsNow, skillsFuture (5 campos)
      totalFields += 5;
      
      if (this.user.professionalSection?.professionalData?.company) {
        completedFields++;
        console.log('✓ company:', this.user.professionalSection.professionalData.company);
      } else {
        missingFields.push('Industria/Sector');
        console.log('✗ company: FALTANTE');
      }
      
      const role = this.getRoleText();
      if (role) {
        completedFields++;
        console.log('✓ job/role:', role);
      } else {
        missingFields.push('Rol/Cargo');
        console.log('✗ job/role: FALTANTE');
      }
      
      if (this.user.professionalSection?.skills) {
        completedFields++;
        console.log('✓ skills:', this.user.professionalSection.skills);
      } else {
        missingFields.push('Habilidades principales');
        console.log('✗ skills: FALTANTE');
      }
      
      if (this.user.professionalSection?.skillsNow) {
        completedFields++;
        console.log('✓ skillsNow:', this.user.professionalSection.skillsNow);
      } else {
        missingFields.push('Habilidades actuales que quiero mejorar');
        console.log('✗ skillsNow: FALTANTE');
      }
      
      if (this.user.professionalSection?.skillsFuture) {
        completedFields++;
        console.log('✓ skillsFuture:', this.user.professionalSection.skillsFuture);
      } else {
        missingFields.push('Habilidades futuras que quiero desarrollar');
        console.log('✗ skillsFuture: FALTANTE');
      }
    } else if (this.shouldShowRetiredFields()) {
      console.log('Usuario es retirado');
      // Para retirados: ultimoCargo, ultimaEmpresa, skills, skillsNow, skillsFuture (5 campos)
      totalFields += 5;
      
      if (this.user.professionalSection?.ultimoCargo) {
        completedFields++;
        console.log('✓ ultimoCargo:', this.user.professionalSection.ultimoCargo);
      } else {
        missingFields.push('Último cargo');
        console.log('✗ ultimoCargo: FALTANTE');
      }
      
      if (this.user.professionalSection?.ultimaEmpresa) {
        completedFields++;
        console.log('✓ ultimaEmpresa:', this.user.professionalSection.ultimaEmpresa);
      } else {
        missingFields.push('Última empresa');
        console.log('✗ ultimaEmpresa: FALTANTE');
      }
      
      if (this.user.professionalSection?.skills) {
        completedFields++;
        console.log('✓ skills:', this.user.professionalSection.skills);
      } else {
        missingFields.push('Habilidades principales');
        console.log('✗ skills: FALTANTE');
      }
      
      if (this.user.professionalSection?.skillsNow) {
        completedFields++;
        console.log('✓ skillsNow:', this.user.professionalSection.skillsNow);
      } else {
        missingFields.push('Habilidades actuales que quiero mejorar');
        console.log('✗ skillsNow: FALTANTE');
      }
      
      if (this.user.professionalSection?.skillsFuture) {
        completedFields++;
        console.log('✓ skillsFuture:', this.user.professionalSection.skillsFuture);
      } else {
        missingFields.push('Habilidades futuras que quiero desarrollar');
        console.log('✗ skillsFuture: FALTANTE');
      }
    }

    // Sección 3: Información personal (3 campos - interactionType deshabilitado temporalmente)
    console.log('--- Sección 3: Información personal ---');
    totalFields += 3;
    
    if (this.user.personalSection?.colors) {
      completedFields++;
      console.log('✓ colors:', this.user.personalSection.colors);
    } else {
      missingFields.push('Colores favoritos');
      console.log('✗ colors: FALTANTE');
    }
    
    if (this.user.personalSection?.travelDestination) {
      completedFields++;
      console.log('✓ travelDestination:', this.user.personalSection.travelDestination);
    } else {
      missingFields.push('Destino de viaje/Tipo de comunidades');
      console.log('✗ travelDestination: FALTANTE');
    }
    
    if (this.user.personalSection?.phoenixExpectations) {
      completedFields++;
      console.log('✓ phoenixExpectations:', this.user.personalSection.phoenixExpectations);
    } else {
      missingFields.push('Expectativas de Phoenix');
      console.log('✗ phoenixExpectations: FALTANTE');
    }

    // Calcular porcentaje
    const percentage = totalFields === 0 ? 0 : Math.round((completedFields / totalFields) * 100);
    
    console.log('=== RESUMEN ===');
    console.log('Campos completados:', completedFields, '/', totalFields);
    console.log('Porcentaje:', percentage + '%');
    
    if (missingFields.length > 0) {
      console.log('❌ CAMPOS FALTANTES PARA COMPLETAR EL 100%:');
      missingFields.forEach((field, index) => {
        console.log(`   ${index + 1}. ${field}`);
      });
    } else {
      console.log('✅ PERFIL COMPLETADO AL 100%');
    }
    console.log('===========================');
    
    return percentage;
  }

  // Método para obtener las opciones de rango salarial desde registerQuestions
  getSalaryRangeOptions(): string[] {
    if (this.user.profile?.registerQuestions?.[4]?.answers) {
      return this.user.profile.registerQuestions[4].answers;
    }
    // Fallback a opciones por defecto si no hay registerQuestions
    return environment.salaryRanges || [];
  }

  // Método para obtener el texto del rango salarial
  getSalaryRangeText(): string {
    // Primero intentar obtener desde registerQuestions[4].value
    if (this.user.profile?.registerQuestions?.[4]?.value) {
      return this.user.profile.registerQuestions[4].value;
    }
    // Fallback a professionalSection.professionalData.salaryRange para compatibilidad
    const salaryRange = this.user.professionalSection?.professionalData?.salaryRange;
    if (!salaryRange) return '';
    return salaryRange;
  }

  // Método para obtener las opciones de situación laboral desde registerQuestions
  getSituacionLaboralOptions(): string[] {
    if (this.user.profile?.registerQuestions?.[3]?.answers) {
      return this.user.profile.registerQuestions[3].answers;
    }
    // Fallback a opciones por defecto si no hay registerQuestions
    return ['Activo laboralmente', 'Independiente', 'Retirado', 'En transición'];
  }

  // Método para obtener el texto de la situación laboral
  getSituacionLaboralText(): string {
    // Primero intentar obtener desde registerQuestions[3].value
    if (this.user.profile?.registerQuestions?.[3]?.value) {
      return this.user.profile.registerQuestions[3].value;
    }
    // Fallback a professionalSection.situacionLaboral para compatibilidad
    const situacion = this.user.professionalSection?.situacionLaboral;
    return situacion || '';
  }

  // Método para obtener el texto de la industria
  getIndustryText(): string {
    const industry = this.user.professionalSection?.professionalData?.company;
    if (!industry) return '';
    
    const industryMap: { [key: string]: string } = {
      'tecnologia': 'Tecnología',
      'salud': 'Salud',
      'educacion': 'Educación',
      'finanzas': 'Finanzas',
      'retail': 'Retail',
      'manufactura': 'Manufactura',
      'consultoria': 'Consultoría',
      'otro': 'Otro'
    };
    
    return industryMap[industry] || industry;
  }

  // Método para obtener las opciones de rol desde registerQuestions
  getRoleOptions(): string[] {
    if (this.user.profile?.registerQuestions?.[5]?.answers) {
      return this.user.profile.registerQuestions[5].answers;
    }
    // Fallback a opciones por defecto si no hay registerQuestions
    return this.roleOptions;
  }

  // Método para obtener el texto del rol
  getRoleText(): string {
    // Primero intentar obtener desde registerQuestions[5].value
    if (this.user.profile?.registerQuestions?.[5]?.value) {
      return this.user.profile.registerQuestions[5].value;
    }
    // Fallback a professionalSection.professionalData.job para compatibilidad
    const role = this.user.professionalSection?.professionalData?.job;
    if (!role) return '';
    
    const roleMap: { [key: string]: string } = {
      'gerente': 'Gerente',
      'director': 'Director',
      'analista': 'Analista',
      'especialista': 'Especialista',
      'coordinador': 'Coordinador',
      'asistente': 'Asistente',
      'consultor': 'Consultor',
      'otro': 'Otro'
    };
    
    return roleMap[role] || role;
  }

  // Método para obtener el texto de habilidades actuales
  getSkillsNowText(): string {
    const skills = this.user.professionalSection?.futureProjects;
    if (!skills) return '';
    
    const skillsMap: { [key: string]: string } = {
      'liderazgo': 'Liderazgo',
      'comunicacion': 'Comunicación',
      'tecnologia': 'Tecnología',
      'gestion': 'Gestión de proyectos',
      'ventas': 'Ventas',
      'marketing': 'Marketing',
      'finanzas': 'Finanzas',
      'otro': 'Otro'
    };
    
    return skillsMap[skills] || skills;
  }

  // Método para obtener el texto de habilidades futuras
  getFutureSkillsText(): string {
    const futureSkills = this.user.professionalSection?.futureSkills;
    if (!futureSkills) return '';
    
    const futureSkillsMap: { [key: string]: string } = {
      'liderazgo-estrategico': 'Liderazgo estratégico',
      'innovacion': 'Innovación',
      'digitalizacion': 'Transformación digital',
      'emprendimiento': 'Emprendimiento',
      'finanzas-avanzadas': 'Finanzas avanzadas',
      'inteligencia-artificial': 'Inteligencia Artificial',
      'otro': 'Otro'
    };
    
    return futureSkillsMap[futureSkills] || futureSkills;
  }

  // Método para continuar al dashboard
  continueToDashboard() {
    this.showProfileCompleteModal = false;
    
    // Establecer bandera para auto-reproducción del video en dashboard
    sessionStorage.setItem('fromProfileComplete', 'true');
    
    this.router.navigate([this.getDashboardRoute()]);
  }

  constructor(private router: Router) {
    super();
  }

  ngOnInit() {
    this.loadUserDetails();
    this.checkIfResponsive();
    window.addEventListener('resize', () => {
      clearTimeout(this.debounceResize);
      this.debounceResize = setTimeout(() => this.checkIfResponsive(), 200);
    });
  }

  /**
   * Carga los detalles del usuario desde la API
   */
  loadUserDetails() {
    this.loader.show();
    const authUser = this.authService.getAuthUser();

    if (authUser) {
      this.currentUserId = authUser.id;
      this.userService.findById(this.currentUserId).subscribe({
        next: (response) => {
          if (response && response.data) {
            this.user = response.data;

            // Cargar la foto de perfil (priorizar photoBase64 sobre photo)
            if (this.user.personalSection && this.user.personalSection.photoBase64) {
              this.profilePhotoUrl = this.user.personalSection.photoBase64;
              console.log('Cargando foto desde personalSection.photoBase64');
            } else if (this.user.photo) {
              this.user.personalSection = this.user.personalSection || {};
              this.profilePhotoUrl = this.user.photo;
              console.log('Cargando foto desde user.photo');
            }

            // Aseguramos que las secciones del perfil estén inicializadas
            this.initializeUserDefaults();
            
            // Sincronizar registerQuestions[3].value con professionalSection.situacionLaboral si es necesario
            this.syncSituacionLaboral();
            
            // Sincronizar registerQuestions[4].value con professionalSection.salaryRange si es necesario
            this.syncSalaryRange();
            
            // Sincronizar registerQuestions[5].value con professionalSection.job si es necesario
            this.syncRole();
            
            // Guardar una copia profunda de los datos iniciales para comparar cambios
            this.saveInitialUserData();
          } else {
            this.error('No se pudo cargar la información del usuario.');
          }
          this.loader.hide();
        },
        error: (err) => {
          console.error('Error al cargar los datos del usuario:', err);
          this.error('Ocurrió un error al cargar los datos del usuario. Por favor, intenta nuevamente.');
          this.loader.hide();
        }
      });
    } else {
      this.loader.hide();
      this.router.navigate(['/auth/login']);
    }
  }

  checkIfResponsive() {
    this.isResponsive = window.innerWidth <= 768;
    if (!this.isResponsive) {
      this.sectionsState = ['expanded', 'expanded', 'expanded'];
    } else {
      this.sectionsState = ['expanded', 'collapsed', 'collapsed'];
    }
  }

  toggleSection(index: number): void {
    if (!this.isResponsive) return;
    this.sectionsState[index] = this.sectionsState[index] === 'expanded' ? 'collapsed' : 'expanded';
  }

  ngAfterViewInit() {
    // Cerrar dropdowns cuando se hace clic fuera de ellos
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      // No cerrar si el clic es en un input de texto dentro de "Otro"
      if (target.closest('.other-input')) {
        return;
      }
      if (!target.closest('.custom-multiselect')) {
        this.showSkillsNowDropdown = false;
        this.showSkillsFutureDropdown = false;
        this.showIndustryDropdown = false;
        this.showCommunityTypeDropdown = false;
        this.showInteractionTypeDropdown = false;
      }
    });
  }

  /**
   * Manejador para cuando se selecciona una foto
   * @param event Evento de cambio del input file
   */
  async onPhotoSelected(event: any) {
    const files = event.target.files as FileList;
    if (files && files.length > 0) {
      this.selectedPhotoFile = files[0];

      try {
        // Convertir el archivo a Base64
        const base64 = await this.userService.fileToBase64(this.selectedPhotoFile);
        this.profilePhotoUrl = base64;
        this.photoChanged = true;
      } catch (error) {
        console.error('Error al convertir la imagen:', error);
        this.error('Ocurrió un error al procesar la imagen. Por favor, intenta nuevamente.');
      }
    }
  }

  /**
   * Elimina la foto de perfil seleccionada
   */
  removePhoto() {
    this.profilePhotoUrl = null;
    this.selectedPhotoFile = null;
    this.photoChanged = true;
    if (this.photoFileInput) {
      this.photoFileInput.nativeElement.value = '';
    }
  }

  /**
   * Sube la foto de perfil al servidor si ha cambiado
   * @returns Promesa que se resuelve cuando se completa la subida
   */
  async uploadProfilePhoto(): Promise<void> {
    if (!this.photoChanged) {
      return Promise.resolve();
    }

    try {
      return new Promise((resolve, reject) => {
        this.userService.updateProfilePhoto(this.currentUserId, this.profilePhotoUrl || '').subscribe({
          next: (response) => {
            this.photoChanged = false;
            resolve();
          },
          error: (err) => {
            console.error('Error al actualizar la foto de perfil:', err);
            reject(err);
          }
        });
      });
    } catch (error) {
      console.error('Error al subir la foto de perfil:', error);
      return Promise.reject(error);
    }
  }

  initializeUserDefaults() {
    // Asegura que todos los campos requeridos estén inicializados
    this.user = {
      ...this.user,
      // Si firstName es null o undefined, usar el campo name como respaldo
      firstName: this.user.firstName || this.user.name || '',
      codePhone: this.user.codePhone || '51',
      phone: this.user.phone || '',
      personalSection: this.user.personalSection || {
        hobbys: '',
        colors: '',
        travelDestination: '',
        newActivities: '',
      },
      professionalSection: this.user.professionalSection || {
        isWorker: false,
        situacionLaboral: '',
        professionalData: {
          company: '',
          job: '',
          salary: {
            id: 0,
            name: '',
          },
          salaryRange: '',
        },
        skills: '',
        futureProjects: '',
        skillsNow: '',
        skillsFuture: '',
        ultimoCargo: '',
        ultimaEmpresa: '',
      },
    };

    // Actualiza el selector de salario si está presente
    if (this.user.professionalSection?.professionalData?.salary?.id) {
      const index = this.user.professionalSection.professionalData.salary.id;
      if (index > 0) {
        this.selectedSalaryOption = index - 1;
      } else {
        this.selectedSalaryOption = null;
      }
    }

    // Inicializar las habilidades seleccionadas desde los datos guardados
    this.initializeSkillsFromData();
    
    // Inicializar campos "Otro" personalizados
    this.initializeOtherFields();
  }

  // Método para inicializar campos "Otro" personalizados
  initializeOtherFields() {
    // Inicializar industria "Otro"
    const company = this.user.professionalSection?.professionalData?.company;
    if (company) {
      if (company === 'Otro sector') {
        // Si ya está marcado como "Otro sector", mostrar el campo pero sin valor personalizado previo
        this.showOtherIndustry = true;
        this.otherIndustry = '';
      } else if (!this.industryOptions.includes(company)) {
        // Si el valor no está en las opciones, es un valor personalizado
        this.showOtherIndustry = true;
        this.otherIndustry = company;
        // Mantener el valor personalizado, no cambiarlo a "Otro sector"
      }
    }

    // Inicializar tipo de comunidades "Otro"
    const travelDestination = this.user.personalSection?.travelDestination;
    if (travelDestination) {
      if (travelDestination === 'Otro') {
        // Si ya está marcado como "Otro", mostrar el campo pero sin valor personalizado previo
        this.showOtherCommunityType = true;
        this.otherCommunityType = '';
      } else if (!this.communityTypeOptions.includes(travelDestination)) {
        // Si el valor no está en las opciones, es un valor personalizado
        this.showOtherCommunityType = true;
        this.otherCommunityType = travelDestination;
        // Mantener el valor personalizado, no cambiarlo a "Otro"
      }
    }

    // Inicializar tipo de interacciones "Otro"
    const interactionType = this.user.personalSection?.interactionType;
    if (interactionType) {
      if (interactionType === 'Otro') {
        // Si ya está marcado como "Otro", mostrar el campo pero sin valor personalizado previo
        this.showOtherInteractionType = true;
        this.otherInteractionType = '';
      } else if (!this.interactionOptions.includes(interactionType)) {
        // Si el valor no está en las opciones, es un valor personalizado
        this.showOtherInteractionType = true;
        this.otherInteractionType = interactionType;
        // Mantener el valor personalizado, no cambiarlo a "Otro"
      }
    }
  }

  // Métodos para toggle de dropdowns
  toggleIndustryDropdown() {
    this.showIndustryDropdown = !this.showIndustryDropdown;
    if (this.showIndustryDropdown) {
      this.showCommunityTypeDropdown = false;
      this.showInteractionTypeDropdown = false;
      this.showSkillsNowDropdown = false;
      this.showSkillsFutureDropdown = false;
    }
  }

  toggleCommunityTypeDropdown() {
    this.showCommunityTypeDropdown = !this.showCommunityTypeDropdown;
    if (this.showCommunityTypeDropdown) {
      this.showIndustryDropdown = false;
      this.showInteractionTypeDropdown = false;
      this.showSkillsNowDropdown = false;
      this.showSkillsFutureDropdown = false;
    }
  }

  toggleInteractionTypeDropdown() {
    this.showInteractionTypeDropdown = !this.showInteractionTypeDropdown;
    if (this.showInteractionTypeDropdown) {
      this.showIndustryDropdown = false;
      this.showCommunityTypeDropdown = false;
      this.showSkillsNowDropdown = false;
      this.showSkillsFutureDropdown = false;
    }
  }

  // Métodos para obtener placeholders
  getIndustryPlaceholder(): string {
    const company = this.user.professionalSection?.professionalData?.company;
    // Si hay un valor personalizado guardado directamente
    if (company && !this.industryOptions.includes(company)) {
      return company;
    }
    // Si está en modo "Otro" y hay texto personalizado
    if (this.showOtherIndustry && this.otherIndustry) {
      return this.otherIndustry;
    }
    // Si hay una opción válida seleccionada
    if (company && this.industryOptions.includes(company)) {
      return company;
    }
    return 'Seleccione una opción';
  }

  getCommunityTypePlaceholder(): string {
    const travelDestination = this.user.personalSection?.travelDestination;
    // Si hay un valor personalizado guardado directamente
    if (travelDestination && !this.communityTypeOptions.includes(travelDestination) && travelDestination !== 'Otro') {
      return travelDestination;
    }
    // Si está en modo "Otro" y hay texto personalizado
    if (this.showOtherCommunityType && this.otherCommunityType) {
      return this.otherCommunityType;
    }
    // Si hay una opción válida seleccionada
    if (travelDestination && this.communityTypeOptions.includes(travelDestination)) {
      return travelDestination;
    }
    return 'Seleccione una opción';
  }

  getInteractionTypePlaceholder(): string {
    const interactionType = this.user.personalSection?.interactionType;
    // Si hay un valor personalizado guardado directamente
    if (interactionType && !this.interactionOptions.includes(interactionType) && interactionType !== 'Otro') {
      return interactionType;
    }
    // Si está en modo "Otro" y hay texto personalizado
    if (this.showOtherInteractionType && this.otherInteractionType) {
      return this.otherInteractionType;
    }
    // Si hay una opción válida seleccionada
    if (interactionType && this.interactionOptions.includes(interactionType)) {
      return interactionType;
    }
    return 'Seleccione una opción';
  }

  // Métodos para verificar si una opción está seleccionada
  isIndustrySelected(option: string): boolean {
    const company = this.user.professionalSection?.professionalData?.company;
    return company === option;
  }

  isCommunityTypeSelected(option: string): boolean {
    const travelDestination = this.user.personalSection?.travelDestination;
    return travelDestination === option;
  }

  isInteractionTypeSelected(option: string): boolean {
    const interactionType = this.user.personalSection?.interactionType;
    return interactionType === option;
  }

  // Métodos para manejar cambios en las selecciones
  onIndustryChange(option: string, event: any) {
    if (option === 'Otro sector') {
      this.showOtherIndustry = true;
      if (!this.otherIndustry) {
        this.otherIndustry = '';
      }
      this.user.professionalSection.professionalData.company = 'Otro sector';
    } else {
      this.showOtherIndustry = false;
      this.otherIndustry = '';
      this.user.professionalSection.professionalData.company = option;
    }
    this.showIndustryDropdown = false;
  }

  toggleOtherIndustry(event: any) {
    event.stopPropagation();
    this.showOtherIndustry = event.target.checked;
    if (this.showOtherIndustry) {
      this.user.professionalSection.professionalData.company = 'Otro sector';
      if (!this.otherIndustry) {
        this.otherIndustry = '';
      }
    } else {
      this.otherIndustry = '';
      this.user.professionalSection.professionalData.company = '';
    }
    // No cerrar el dropdown para permitir escribir en el input
  }

  onOtherIndustryChange() {
    if (this.otherIndustry && this.otherIndustry.trim()) {
      this.user.professionalSection.professionalData.company = this.otherIndustry.trim();
    }
  }

  onCommunityTypeChange(option: string, event: any) {
    if (option === 'Otro') {
      this.showOtherCommunityType = true;
      if (!this.otherCommunityType) {
        this.otherCommunityType = '';
      }
      this.user.personalSection.travelDestination = 'Otro';
    } else {
      this.showOtherCommunityType = false;
      this.otherCommunityType = '';
      this.user.personalSection.travelDestination = option;
    }
    this.showCommunityTypeDropdown = false;
  }

  toggleOtherCommunityType(event: any) {
    event.stopPropagation();
    this.showOtherCommunityType = event.target.checked;
    if (this.showOtherCommunityType) {
      this.user.personalSection.travelDestination = 'Otro';
      if (!this.otherCommunityType) {
        this.otherCommunityType = '';
      }
    } else {
      this.otherCommunityType = '';
      this.user.personalSection.travelDestination = '';
    }
    // No cerrar el dropdown para permitir escribir en el input
  }

  onOtherCommunityTypeChange() {
    if (this.otherCommunityType && this.otherCommunityType.trim()) {
      this.user.personalSection.travelDestination = this.otherCommunityType.trim();
    }
  }

  onInteractionTypeChange(option: string, event: any) {
    if (option === 'Otro') {
      this.showOtherInteractionType = true;
      if (!this.otherInteractionType) {
        this.otherInteractionType = '';
      }
      this.user.personalSection.interactionType = 'Otro';
    } else {
      this.showOtherInteractionType = false;
      this.otherInteractionType = '';
      this.user.personalSection.interactionType = option;
    }
    this.showInteractionTypeDropdown = false;
  }

  toggleOtherInteractionType(event: any) {
    event.stopPropagation();
    this.showOtherInteractionType = event.target.checked;
    if (this.showOtherInteractionType) {
      this.user.personalSection.interactionType = 'Otro';
      if (!this.otherInteractionType) {
        this.otherInteractionType = '';
      }
    } else {
      this.otherInteractionType = '';
      this.user.personalSection.interactionType = '';
    }
    // No cerrar el dropdown para permitir escribir en el input
  }

  onOtherInteractionTypeChange() {
    if (this.otherInteractionType && this.otherInteractionType.trim()) {
      this.user.personalSection.interactionType = this.otherInteractionType.trim();
    }
  }

  // Método para sincronizar situacionLaboral entre registerQuestions[3] y professionalSection
  syncSituacionLaboral() {
    // Asegurar que profile.registerQuestions existe
    if (!this.user.profile) {
      this.user.profile = {
        registerQuestionsComplete: false,
        testNowComplete: false,
        testFutureComplete: false,
        onboardingComplete: false,
        registerQuestions: []
      };
    }

    // Asegurar que registerQuestions[3] existe
    if (!this.user.profile.registerQuestions || this.user.profile.registerQuestions.length <= 3) {
      if (!this.user.profile.registerQuestions) {
        this.user.profile.registerQuestions = [];
      }
      // Inicializar registerQuestions[3] si no existe
      while (this.user.profile.registerQuestions.length <= 3) {
        this.user.profile.registerQuestions.push({ title: '', answers: [], value: '' });
      }
    }

    // Si hay valor en professionalSection.situacionLaboral pero no en registerQuestions[3].value
    if (this.user.professionalSection?.situacionLaboral && !this.user.profile.registerQuestions[3].value) {
      this.user.profile.registerQuestions[3].value = this.user.professionalSection.situacionLaboral;
    }
    // Si hay valor en registerQuestions[3].value pero no en professionalSection.situacionLaboral
    else if (this.user.profile.registerQuestions[3].value && !this.user.professionalSection?.situacionLaboral) {
      if (!this.user.professionalSection) {
        this.user.professionalSection = {};
      }
      this.user.professionalSection.situacionLaboral = this.user.profile.registerQuestions[3].value;
    }
  }

  // Método para sincronizar role/job entre registerQuestions[5] y professionalSection
  syncRole() {
    // Asegurar que profile.registerQuestions existe
    if (!this.user.profile) {
      this.user.profile = {
        registerQuestionsComplete: false,
        testNowComplete: false,
        testFutureComplete: false,
        onboardingComplete: false,
        registerQuestions: []
      };
    }

    // Asegurar que registerQuestions[5] existe
    if (!this.user.profile.registerQuestions || this.user.profile.registerQuestions.length <= 5) {
      if (!this.user.profile.registerQuestions) {
        this.user.profile.registerQuestions = [];
      }
      // Inicializar registerQuestions[5] si no existe
      while (this.user.profile.registerQuestions.length <= 5) {
        this.user.profile.registerQuestions.push({ title: '', answers: [], value: '' });
      }
    }

    // Si hay valor en professionalSection.professionalData.job pero no en registerQuestions[5].value
    if (this.user.professionalSection?.professionalData?.job && !this.user.profile.registerQuestions[5].value) {
      this.user.profile.registerQuestions[5].value = this.user.professionalSection.professionalData.job;
    }
    // Si hay valor en registerQuestions[5].value pero no en professionalSection.professionalData.job
    else if (this.user.profile.registerQuestions[5].value && !this.user.professionalSection?.professionalData?.job) {
      if (!this.user.professionalSection) {
        this.user.professionalSection = {};
      }
      if (!this.user.professionalSection.professionalData) {
        this.user.professionalSection.professionalData = {};
      }
      this.user.professionalSection.professionalData.job = this.user.profile.registerQuestions[5].value;
    }
  }

  // Método para sincronizar salaryRange entre registerQuestions[4] y professionalSection
  syncSalaryRange() {
    // Asegurar que profile.registerQuestions existe
    if (!this.user.profile) {
      this.user.profile = {
        registerQuestionsComplete: false,
        testNowComplete: false,
        testFutureComplete: false,
        onboardingComplete: false,
        registerQuestions: []
      };
    }

    // Asegurar que registerQuestions[4] existe
    if (!this.user.profile.registerQuestions || this.user.profile.registerQuestions.length <= 4) {
      if (!this.user.profile.registerQuestions) {
        this.user.profile.registerQuestions = [];
      }
      // Inicializar registerQuestions[4] si no existe
      while (this.user.profile.registerQuestions.length <= 4) {
        this.user.profile.registerQuestions.push({ title: '', answers: [], value: '' });
      }
    }

    // Si hay valor en professionalSection.professionalData.salaryRange pero no en registerQuestions[4].value
    if (this.user.professionalSection?.professionalData?.salaryRange && !this.user.profile.registerQuestions[4].value) {
      this.user.profile.registerQuestions[4].value = this.user.professionalSection.professionalData.salaryRange;
    }
    // Si hay valor en registerQuestions[4].value pero no en professionalSection.professionalData.salaryRange
    else if (this.user.profile.registerQuestions[4].value && !this.user.professionalSection?.professionalData?.salaryRange) {
      if (!this.user.professionalSection) {
        this.user.professionalSection = {};
      }
      if (!this.user.professionalSection.professionalData) {
        this.user.professionalSection.professionalData = {};
      }
      this.user.professionalSection.professionalData.salaryRange = this.user.profile.registerQuestions[4].value;
    }
  }

  // Método para obtener la situación laboral actual (desde registerQuestions o fallback)
  getCurrentSituacionLaboral(): string {
    if (this.user.profile?.registerQuestions?.[3]?.value) {
      return this.user.profile.registerQuestions[3].value;
    }
    return this.user.professionalSection?.situacionLaboral || '';
  }

  // Método para verificar si debe mostrar campos de trabajadores activos
  shouldShowWorkerFields(): boolean {
    const situacion = this.getCurrentSituacionLaboral();
    return situacion === 'Activo laboralmente' || situacion === 'Independiente' || 
           situacion === 'Dependiente'; // Mantener compatibilidad con valores antiguos
  }

  // Método para verificar si debe mostrar campos de retirados
  shouldShowRetiredFields(): boolean {
    const situacion = this.getCurrentSituacionLaboral();
    return situacion === 'Retirado';
  }

  getDisabledSectProfessional() {
    // Por defecto, asumimos que está habilitado si no hay información específica
    // Esto puede ser ajustado según la lógica de negocio específica
    return this.user?.professionalSection?.isWorker !== false;
  }

  selectOption(option: boolean) {
    if (option === this.user.professionalSection?.isWorker) return;
    if (!option) {
      this.user.professionalSection.professionalData.company = '';
      this.user.professionalSection.professionalData.job = '';
      this.user.professionalSection.professionalData.salary = { id: 0, name: '' };
      this.user.professionalSection.professionalData.salaryRange = '';
      this.selectedSalaryOption = null;
    }
    this.user.professionalSection.isWorker = option;
  }

  selectSalaryOption(index: number) {
    this.selectedSalaryOption = index;
    this.user.professionalSection.professionalData.salary = SALARY_RANGES[index];
  }

  changeEditName() {
    this.isEditName = !this.isEditName;
  }

  // Función para validar campos requeridos de la sección profesional
  validateProfessionalSection(): boolean {
    console.log('Validando sección profesional...');
    console.log('Datos del usuario:', this.user.professionalSection);
    
    const requiredFields = [
      { 
        field: this.user.professionalSection?.situacionLaboral, 
        input: this.situacionLaboralInput,
        element: this.situacionLaboralElement,
        name: 'Situación laboral'
      },
      { 
        field: this.user.professionalSection?.professionalData?.salaryRange, 
        input: this.salaryRangeInput,
        element: this.salaryRangeElement,
        name: 'Rango salarial'
      }
    ];

    // Si es trabajador activo, agregar campos adicionales
    if (this.shouldShowWorkerFields()) {
      console.log('Es trabajador activo, agregando campos adicionales');
      requiredFields.push(
        { 
          field: this.user.professionalSection?.professionalData?.company, 
          input: this.companyInput,
          element: this.companyElement,
          name: 'Industria'
        },
        { 
          field: this.user.professionalSection?.professionalData?.job, 
          input: this.roleInput,
          element: this.roleElement,
          name: 'Rol'
        }
      );
    }
    
    console.log('Campos a validar:', requiredFields.map(f => ({ name: f.name, value: f.field })));

    let hasErrors = false;
    const emptyFields: string[] = [];

    for (const field of requiredFields) {
      if (!field.field || field.field === '') {
        if (field.input) {
          field.input.control.markAsTouched();
          field.input.control.updateValueAndValidity();
        }
        hasErrors = true;
        emptyFields.push(field.name);
      }
    }

    if (hasErrors) {
      this.showGlobalError = true;
      this.globalErrorMessage = `Por favor, complete los siguientes campos requeridos: ${emptyFields.join(', ')}`;
      
      // Hacer focus y scroll al primer campo vacío
      const firstEmptyField = requiredFields.find(f => !f.field || f.field === '');
      if (firstEmptyField) {
        setTimeout(() => {
          let element = firstEmptyField.element?.nativeElement;
          
          // Si no se encuentra el elemento por ViewChild, intentar por selector
          if (!element) {
            const fieldName = firstEmptyField.name.toLowerCase().replace(/\s+/g, '');
            if (fieldName.includes('situación') || fieldName.includes('situacion')) {
              element = document.querySelector('select[name="situacionLaboral"]') as HTMLElement;
            } else if (fieldName.includes('salarial') || fieldName.includes('salary')) {
              element = document.querySelector('select[name="salaryRange"]') as HTMLElement;
            } else if (fieldName.includes('industria') || fieldName.includes('company')) {
              element = document.querySelector('select[name="company"]') as HTMLElement;
            } else if (fieldName.includes('rol') || fieldName.includes('role')) {
              element = document.querySelector('select[name="job"]') as HTMLElement;
            }
          }
          
          if (element) {
            // Para móvil, usar scroll más agresivo
            const isMobile = window.innerWidth <= 768;
            
            if (isMobile) {
              // En móvil, hacer scroll más directo
              element.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start',
                inline: 'nearest'
              });
              
              // Ajustar scroll para compensar el footer fijo
              setTimeout(() => {
                const currentScroll = window.pageYOffset;
                const adjustedScroll = currentScroll - 100; // Compensar por el footer
                window.scrollTo({
                  top: adjustedScroll,
                  behavior: 'smooth'
                });
              }, 200);
            } else {
              // En desktop, usar scroll centrado
              element.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center',
                inline: 'nearest'
              });
            }
            
            // Hacer focus al elemento después del scroll
            setTimeout(() => {
              if (element && element.focus) {
                element.focus();
                // Para móvil, también intentar click para abrir el dropdown
                if (isMobile && element.tagName === 'SELECT') {
                  element.click();
                }
              }
            }, isMobile ? 500 : 300);
          }
        }, 100);
      }
    }

    return !hasErrors;
  }

  async saveChanges() {
    this.loader.show();

    try {
      // Primero subir la foto de perfil si ha cambiado
      if (this.photoChanged) {
        await this.uploadProfilePhoto();
      }

      // Sincronizar registerQuestions[3].value con professionalSection.situacionLaboral
      if (this.user.profile?.registerQuestions?.[3]?.value) {
        if (!this.user.professionalSection) {
          this.user.professionalSection = {};
        }
        this.user.professionalSection.situacionLaboral = this.user.profile.registerQuestions[3].value;
      }

      // Sincronizar registerQuestions[4].value con professionalSection.professionalData.salaryRange
      if (this.user.profile?.registerQuestions?.[4]?.value) {
        if (!this.user.professionalSection) {
          this.user.professionalSection = {};
        }
        if (!this.user.professionalSection.professionalData) {
          this.user.professionalSection.professionalData = {};
        }
        this.user.professionalSection.professionalData.salaryRange = this.user.profile.registerQuestions[4].value;
      }

      // Sincronizar registerQuestions[5].value con professionalSection.professionalData.job
      if (this.user.profile?.registerQuestions?.[5]?.value) {
        if (!this.user.professionalSection) {
          this.user.professionalSection = {};
        }
        if (!this.user.professionalSection.professionalData) {
          this.user.professionalSection.professionalData = {};
        }
        this.user.professionalSection.professionalData.job = this.user.profile.registerQuestions[5].value;
      }

      // Calcular si la información profesional está completa
      const isCompleteInfo = this.isProfessionalInfoComplete();
      console.log('Información profesional completa:', isCompleteInfo);
      localStorage.setItem('isCompleteInfo', isCompleteInfo.toString());

      // Asegurar que se actualizan ambos campos: firstName y name
      const userData = {
        ...this.user,
        _id: this.user._id || this.user.id,
        name: this.user.firstName, // Aseguramos que name se actualice con el valor de firstName
        isCompleteInfo: isCompleteInfo, // Agregar el campo de información completa
        personalSection: {
          ...this.user.personalSection,
          completedProfile: true, // Añadimos el campo para indicar que el perfil está completo
          photoBase64: this.profilePhotoUrl // Asegurar que photoBase64 se actualiza correctamente
        },
        profile: {
          ...this.user.profile,
          registerQuestions: this.user.profile?.registerQuestions || [],
          LinkedInURL: this.user.profile?.LinkedInURL || ''
        }
      };

      this.userService.editProfile(userData).subscribe({
        next: async (res: any) => {
          // Solo mostrar el mensaje si realmente hubo cambios
          if (this.hasDataChanged()) {
            this.success('Perfil actualizado correctamente');
          }

          // Refrescar los datos del usuario en el servicio de autenticación
          await this.authService.refreshUserData();

          this.loader.hide();

          // Desactivar todos los modos de edición para regresar a la vista de solo lectura
          this.isEditMode = false;
          this.isPersonalEditMode = false;
          this.isProfessionalEditMode = false;
          this.isEditName = false;

          // Recargar los datos del usuario para mostrar la información actualizada
          this.loadUserDetails();
          
          // Resetear el flag de foto cambiada después de guardar exitosamente
          this.photoChanged = false;

          // Mostrar modal de perfil completado solo si todos los formularios están completos
          if (this.isProfileComplete()) {
            this.showProfileCompleteModal = true;
          }
        },
        error: (err: any) => {
          console.error('Error al guardar los cambios:', err);
          this.error('Ha ocurrido un error al guardar los cambios. Por favor, intenta nuevamente.');
          this.loader.hide();
        }
      });
    } catch (error) {
      console.error('Error al procesar la foto de perfil:', error);
      this.error('Ocurrió un error al procesar la foto de perfil. Por favor, intenta nuevamente.');
      this.loader.hide();
    }
  }

  cancelChanges() {
    // Salir de todos los modos de edición
    this.isEditMode = false;
    this.isPersonalEditMode = false;
    this.isProfessionalEditMode = false;
    this.isEditName = false;
    
    // Resetear el flag de foto cambiada
    this.photoChanged = false;
    
    // Recargar los datos del usuario para descartar cambios
    this.loadUserDetails();
  }

  // Nuevos métodos para manejar modos de edición
  toggleEditMode() {
    this.isEditMode = !this.isEditMode;
    if (!this.isEditMode) {
      // Si se sale del modo edición, resetear cambios
      this.loadUserDetails();
    }
  }

  togglePersonalEdit() {
    this.isPersonalEditMode = !this.isPersonalEditMode;
    if (!this.isPersonalEditMode) {
      // Si se sale del modo edición, resetear cambios
      this.loadUserDetails();
    }
  }

  toggleProfessionalEdit() {
    this.isProfessionalEditMode = !this.isProfessionalEditMode;
    // Limpiar errores al cambiar de modo
    this.showGlobalError = false;
    this.globalErrorMessage = '';
    
    if (!this.isProfessionalEditMode) {
      // Si se sale del modo edición, resetear cambios
      this.loadUserDetails();
    }
  }

  toggleGeneralEdit() {
    const isAnyEditMode = this.isEditMode || this.isPersonalEditMode || this.isProfessionalEditMode;
    
    if (isAnyEditMode) {
      // Si alguna sección está en modo edición, desactivar todas
      this.isEditMode = false;
      this.isPersonalEditMode = false;
      this.isProfessionalEditMode = false;
      this.showGlobalError = false;
      this.globalErrorMessage = '';
      this.saveChanges();
      // Resetear cambios
      this.loadUserDetails();
    } else {
      // Activar todas las secciones en modo edición
      this.isEditMode = true;
      this.isPersonalEditMode = true;
      this.isProfessionalEditMode = true;
      this.showGlobalError = false;
      this.globalErrorMessage = '';
    }
  }

  isGeneralEditMode(): boolean {
    return this.isEditMode || this.isPersonalEditMode || this.isProfessionalEditMode;
  }

  // Métodos para manejar selecciones múltiples de habilidades
  onSkillNowChange(skill: string, event: any) {
    if (skill === 'Otro') {
      this.showOtherSkillNow = event.target.checked;
      if (!this.showOtherSkillNow) {
        this.otherSkillNow = '';
      }
    }
    
    if (event.target.checked) {
      if (!this.selectedSkillsNow.includes(skill)) {
        this.selectedSkillsNow.push(skill);
      }
    } else {
      this.selectedSkillsNow = this.selectedSkillsNow.filter(s => s !== skill);
    }
    
    this.updateSkillsNowString();
  }

  onSkillFutureChange(skill: string, event: any) {
    if (skill === 'Otro') {
      this.showOtherSkillFuture = event.target.checked;
      if (!this.showOtherSkillFuture) {
        this.otherSkillFuture = '';
      }
    }
    
    if (event.target.checked) {
      if (!this.selectedSkillsFuture.includes(skill)) {
        this.selectedSkillsFuture.push(skill);
      }
    } else {
      this.selectedSkillsFuture = this.selectedSkillsFuture.filter(s => s !== skill);
    }
    
    this.updateSkillsFutureString();
  }

  updateSkillsNowString() {
    let skills = [...this.selectedSkillsNow];
    if (this.showOtherSkillNow && this.otherSkillNow.trim()) {
      skills = skills.filter(s => s !== 'Otro');
      skills.push(this.otherSkillNow.trim());
    }
    this.user.professionalSection.skillsNow = skills.join(', ');
  }

  updateSkillsFutureString() {
    let skills = [...this.selectedSkillsFuture];
    if (this.showOtherSkillFuture && this.otherSkillFuture.trim()) {
      skills = skills.filter(s => s !== 'Otro');
      skills.push(this.otherSkillFuture.trim());
    }
    this.user.professionalSection.skillsFuture = skills.join(', ');
  }

  isSkillNowSelected(skill: string): boolean {
    return this.selectedSkillsNow.includes(skill);
  }

  isSkillFutureSelected(skill: string): boolean {
    return this.selectedSkillsFuture.includes(skill);
  }

  // Métodos para manejar los dropdowns de multi-select
  toggleSkillsNowDropdown() {
    this.showSkillsNowDropdown = !this.showSkillsNowDropdown;
    if (this.showSkillsNowDropdown) {
      this.showSkillsFutureDropdown = false; // Cerrar el otro dropdown
    }
  }

  toggleSkillsFutureDropdown() {
    this.showSkillsFutureDropdown = !this.showSkillsFutureDropdown;
    if (this.showSkillsFutureDropdown) {
      this.showSkillsNowDropdown = false; // Cerrar el otro dropdown
    }
  }

  // Métodos para obtener el placeholder de los multi-select
  getSkillsNowPlaceholder(): string {
    if (this.selectedSkillsNow.length === 0 && !this.showOtherSkillNow) {
      return 'Seleccione una opción';
    }
    
    let selected = [...this.selectedSkillsNow];
    if (this.showOtherSkillNow && this.otherSkillNow.trim()) {
      selected.push(this.otherSkillNow.trim());
    }
    
    if (selected.length === 1) {
      return selected[0];
    } else if (selected.length > 1) {
      return `${selected.length} opciones seleccionadas`;
    }
    
    return 'Seleccione una opción';
  }

  getSkillsFuturePlaceholder(): string {
    if (this.selectedSkillsFuture.length === 0 && !this.showOtherSkillFuture) {
      return 'Seleccione una opción';
    }
    
    let selected = [...this.selectedSkillsFuture];
    if (this.showOtherSkillFuture && this.otherSkillFuture.trim()) {
      selected.push(this.otherSkillFuture.trim());
    }
    
    if (selected.length === 1) {
      return selected[0];
    } else if (selected.length > 1) {
      return `${selected.length} opciones seleccionadas`;
    }
    
    return 'Seleccione una opción';
  }

  // Métodos para manejar la opción "Otro"
  toggleOtherSkillNow(event: any) {
    this.showOtherSkillNow = event.target.checked;
    if (!this.showOtherSkillNow) {
      this.otherSkillNow = '';
      this.updateSkillsNowString();
    }
  }

  toggleOtherSkillFuture(event: any) {
    this.showOtherSkillFuture = event.target.checked;
    if (!this.showOtherSkillFuture) {
      this.otherSkillFuture = '';
      this.updateSkillsFutureString();
    }
  }

  // Método para inicializar las habilidades seleccionadas desde los datos guardados
  initializeSkillsFromData() {
    // Inicializar habilidades actuales
    if (this.user.professionalSection?.skillsNow) {
      const skillsNowArray = this.user.professionalSection.skillsNow.split(', ').filter((s: string) => s.trim());
      this.selectedSkillsNow = [];
      this.otherSkillNow = '';
      this.showOtherSkillNow = false;

      skillsNowArray.forEach((skill: string) => {
        if (this.skillsOptions.slice(0, -1).includes(skill.trim())) {
          this.selectedSkillsNow.push(skill.trim());
        } else {
          // Es una habilidad personalizada
          this.showOtherSkillNow = true;
          this.otherSkillNow = skill.trim();
        }
      });
    }

    // Inicializar habilidades futuras
    if (this.user.professionalSection?.skillsFuture) {
      const skillsFutureArray = this.user.professionalSection.skillsFuture.split(', ').filter((s: string) => s.trim());
      this.selectedSkillsFuture = [];
      this.otherSkillFuture = '';
      this.showOtherSkillFuture = false;

      skillsFutureArray.forEach((skill: string) => {
        if (this.skillsOptions.slice(0, -1).includes(skill.trim())) {
          this.selectedSkillsFuture.push(skill.trim());
        } else {
          // Es una habilidad personalizada
          this.showOtherSkillFuture = true;
          this.otherSkillFuture = skill.trim();
        }
      });
    }
  }

  // Método para guardar una copia profunda de los datos iniciales
  saveInitialUserData() {
    this.initialUserData = JSON.parse(JSON.stringify({
      firstName: this.user.firstName,
      lastName: this.user.lastName,
      phone: this.user.phone,
      codePhone: this.user.codePhone,
      personalSection: {
        hobbys: this.user.personalSection?.hobbys,
        colors: this.user.personalSection?.colors,
        travelDestination: this.user.personalSection?.travelDestination,
        newActivities: this.user.personalSection?.newActivities,
        photoBase64: this.user.personalSection?.photoBase64,
        interactionType: this.user.personalSection?.interactionType,
        phoenixExpectations: this.user.personalSection?.phoenixExpectations
      },
      professionalSection: {
        situacionLaboral: this.user.professionalSection?.situacionLaboral,
        ultimoCargo: this.user.professionalSection?.ultimoCargo,
        ultimaEmpresa: this.user.professionalSection?.ultimaEmpresa,
        professionalData: {
          company: this.user.professionalSection?.professionalData?.company,
          job: this.user.professionalSection?.professionalData?.job,
          salaryRange: this.user.professionalSection?.professionalData?.salaryRange
        },
        skills: this.user.professionalSection?.skills,
        skillsNow: this.user.professionalSection?.skillsNow,
        skillsFuture: this.user.professionalSection?.skillsFuture,
        futureProjects: this.user.professionalSection?.futureProjects
      },
      profile: {
        registerQuestions: this.user.profile?.registerQuestions ? 
          JSON.parse(JSON.stringify(this.user.profile.registerQuestions)) : [],
        LinkedInURL: this.user.profile?.LinkedInURL || ''
      }
    }));
  }

  // Método para validar si la información profesional está completa
  isProfessionalInfoComplete(): boolean {
    const professionalSection = this.user.professionalSection;
    
    // Validar que exista la sección profesional
    if (!professionalSection) {
      return false;
    }

    // Validar que situacionLaboral no esté vacío (desde registerQuestions o professionalSection)
    const situacionLaboral = this.getCurrentSituacionLaboral();
    if (!situacionLaboral || situacionLaboral.trim() === '') {
      return false;
    }

    // Validar que salaryRange no esté vacío (desde registerQuestions o professionalSection)
    const salaryRange = this.getSalaryRangeText();
    if (!salaryRange || salaryRange.trim() === '') {
      return false;
    }

    // Validaciones específicas según situación laboral
    if (situacionLaboral === 'Retirado') {
      // Para retirados: validar ultimoCargo y ultimaEmpresa
      if (!professionalSection.ultimoCargo || professionalSection.ultimoCargo.trim() === '') {
        return false;
      }
      if (!professionalSection.ultimaEmpresa || professionalSection.ultimaEmpresa.trim() === '') {
        return false;
      }
    } else {
      // Para no retirados: validar job
      if (!professionalSection.professionalData?.job || 
          professionalSection.professionalData.job.trim() === '') {
        return false;
      }
    }

    return true;
  }

  // Método para comparar si hay cambios en los datos
  hasDataChanged(): boolean {
    const currentData = {
      firstName: this.user.firstName,
      lastName: this.user.lastName,
      phone: this.user.phone,
      codePhone: this.user.codePhone,
      personalSection: {
        hobbys: this.user.personalSection?.hobbys,
        colors: this.user.personalSection?.colors,
        travelDestination: this.user.personalSection?.travelDestination,
        newActivities: this.user.personalSection?.newActivities,
        photoBase64: this.user.personalSection?.photoBase64,
        interactionType: this.user.personalSection?.interactionType,
        phoenixExpectations: this.user.personalSection?.phoenixExpectations
      },
      professionalSection: {
        situacionLaboral: this.user.professionalSection?.situacionLaboral,
        ultimoCargo: this.user.professionalSection?.ultimoCargo,
        ultimaEmpresa: this.user.professionalSection?.ultimaEmpresa,
        professionalData: {
          company: this.user.professionalSection?.professionalData?.company,
          job: this.user.professionalSection?.professionalData?.job,
          salaryRange: this.user.professionalSection?.professionalData?.salaryRange
        },
        skills: this.user.professionalSection?.skills,
        skillsNow: this.user.professionalSection?.skillsNow,
        skillsFuture: this.user.professionalSection?.skillsFuture,
        futureProjects: this.user.professionalSection?.futureProjects
      },
      profile: {
        registerQuestions: this.user.profile?.registerQuestions ? 
          JSON.parse(JSON.stringify(this.user.profile.registerQuestions)) : [],
        LinkedInURL: this.user.profile?.LinkedInURL || ''
      }
    };

    // Comparar si hay cambios en la foto
    if (this.photoChanged) {
      return true;
    }

    // Comparar los datos usando JSON.stringify para comparación profunda
    return JSON.stringify(currentData) !== JSON.stringify(this.initialUserData);
  }

  // Método simplificado para verificar si hay cambios (usado en el template)
  hasChanges(): boolean {
    return this.hasDataChanged();
  }

  /**
   * Verifica si el usuario es Executive
   */
  isExecutiveUser(): boolean {
    return !!(
      this.user &&
      this.user.type &&
      this.user.type.id === 1 && // Customer
      this.user.type.suscriptionType &&
      this.user.type.suscriptionType.id === SUBSCRIPTION_TYPES.Executive.id.toString()
    );
  }

  /**
   * Obtiene la ruta correcta para el dashboard
   */
  getDashboardRoute(): string {
    return this.isExecutiveUser() ? '/dashboard/dashboard-executive' : '/dashboard/dashboard-menu';
  }

  /**
   * Valida que solo se ingresen números en el campo de teléfono
   * @param event Evento del teclado
   */
  onlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    // Permitir solo números (códigos 48-57)
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    }
    return true;
  }
}
